import { createReducer, on } from '@ngrx/store';
import { ODataParams, ODataResponse, odataParamsFactory } from '@tdw/core';
import { FittingAssociatedProduct, FittingProduct, FittingsAdminFilter, fittingProductFactory, fittingsAdminFilterFactory } from 'src/app/models';
import * as fromStore from '../../../store';
import { ProductConfiguration } from 'src/app/models/fitting-query-params.model';
import { Loaders } from '.';


export interface State {
  currentlyEditing: { fittingProduct: FittingProduct | null, associatedProducts?: FittingAssociatedProduct[] };
  fittings?: ODataResponse<FittingProduct[]>;
  configurations?: ODataResponse<ProductConfiguration[]>;
  fittingsQuery: ODataParams;
  configurationsQuery: ODataParams;
  configurationUiFilter: { suffix?: string, productNumber?: string, productLine?: string, isActive?: boolean | string };
  fittingsAdminFilter: FittingsAdminFilter;
  selectedConfigurations: number[];
  associatedProductsQueryResult: FittingAssociatedProduct[];
  loaders: Loaders[];
}

export const initialState: State = {
  associatedProductsQueryResult: [],
  currentlyEditing: { fittingProduct: null, associatedProducts: [] },
  selectedConfigurations: [],
  configurationUiFilter: { isActive: undefined },
  fittingsAdminFilter: fittingsAdminFilterFactory({}),
  fittingsQuery: odataParamsFactory({
    top: 50,
    expand: 'FittingType,Hemisphere,FittingPipeSize,FittingClass,FittingDesignFactor,FittingBranchSize,FittingFlangeType,FittingMaxCe',
    orderBy: 'id desc',
    select: ['id', 'partNumber', 'productName', 'description', 'pipeSizeId', 'classId', 'designFactorId', 'branchSizeId', 'flangeTypeId', 'maxDesignPressure', 'minDesignTemp', 'maxDesignTemp', 'maxCeid', 'limit125', 'notes', 'restricted', 'restrictedNotice', 'inactiveDate', 'createdDate', 'createdBy', 'updatedDate', 'updatedBy', 'fittingTypeId', 'hemisphereId', 'isPreferred'],
    skip: 0,
    count: true
  }),
  configurationsQuery: odataParamsFactory({
    top: 50,
    orderBy: 'suffix,productNumber',
    select: ['id', 'suffix', 'isActive', 'productNumber', 'productLine'],
    expand: 'AvailableConfiguration($expand=AvailableConfigurationFilters($select=name,value))',
    skip: 0,
    count: true
  }),
  loaders: []
};

export const reducer = createReducer(
  initialState,
  on(fromStore.actions.ADMIN.loadFittings, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingFittings)
  })),
  on(fromStore.actions.ADMIN.loadFittingsSuccess, (state: State, action) => ({
    ...state,
    fittings: action.fittings,
    loaders: state.loaders.filter(l => l !== Loaders.loadingFittings)
  })),
  on(fromStore.actions.ADMIN.loadFittingsFail, (state: State, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadingFittings)
  })),
  on(fromStore.actions.ADMIN.loadFittingsForPage, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingFittings),
    fittingsQuery: {
      ...state.fittingsQuery,
      skip: (state.fittingsQuery.top * (action.page - 1))
    },
  })),
  on(fromStore.actions.ADMIN.loadConfigurationsForPage, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadConfigurations),
    configurationsQuery: {
      ...state.configurationsQuery,
      skip: (state.configurationsQuery.top * (action.page - 1))
    },
  })),
  on(fromStore.actions.ADMIN.loadConfigurations, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadConfigurations)
  })),
  on(fromStore.actions.ADMIN.loadConfigurationsSuccess, (state: State, action) => ({
    ...state,
    configurations: action.configurations,
    loaders: state.loaders.filter(l => l !== Loaders.loadConfigurations)
  })),
  on(fromStore.actions.ADMIN.loadConfigurationsFail, (state: State, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadConfigurations)
  })),
  on(fromStore.actions.ADMIN.changeFilterValue, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadConfigurations),
    configurationUiFilter: {
      ...state.configurationUiFilter,
      suffix: action.key === 'suffix' ? action.value?.toString() : state.configurationUiFilter.suffix,
      productNumber: action.key === 'productNumber' ? action.value?.toString() : state.configurationUiFilter.productNumber,
      productLine: action.key === 'productLine' ? action.value?.toString() : state.configurationUiFilter.productLine,
      isActive: action.key === 'isActive' ? action.value : state.configurationUiFilter.isActive
    },
    configurationsQuery: {
      ...state.configurationsQuery,
      skip: 0 // reset the skip back to 0 so it goes back to page 1
    }
  })),
  on(fromStore.actions.ADMIN.updateFittingsAdminFilter, (state: State, action) => ({
    ...state,
    fittingsAdminFilter: Object.assign({}, state.fittingsAdminFilter, action.obj)
  })),
  on(fromStore.actions.ADMIN.loadCurrentlyEditingFitting, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingMaintainenceEditorFitting),
    currentlyEditing: {
      ...state.currentlyEditing,
      fittingProduct: !!action.fittingId ? state.currentlyEditing.fittingProduct : fittingProductFactory({}),
      associatedProducts: !!action.fittingId ? state.currentlyEditing.associatedProducts : []
    }
  })),
  on(fromStore.actions.ADMIN.loadCurrentlyEditingFittingSuccess, (state: State, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingMaintainenceEditorFitting),
    currentlyEditing: {
      ...state.currentlyEditing,
      fittingProduct: action.fitting
    }
  })),
  on(fromStore.actions.ADMIN.loadCurrentlyEditingAssociatedProductsSuccess, (state: State, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingMaintenanceEditorAssociatedProducts),
    currentlyEditing: {
      ...state.currentlyEditing,
      associatedProducts: action.associatedProducts || []
    }
  })),
  on(fromStore.actions.ADMIN.queryAssociatedProducts, (state: State, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingAssociatedProductsForAdmin)
  })),
  on(fromStore.actions.ADMIN.queryAssociatedProductsSuccess, (state: State, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingAssociatedProductsForAdmin),
    associatedProductsQueryResult: action.products
  })),
  on(fromStore.actions.ADMIN.queryAssociatedProductsFail, (state: State, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingAssociatedProductsForAdmin)
  })),
);
